import { Injectable, inject } from '@angular/core';
import createDebug from 'debug';
import { AuthService } from '../auth/auth.service';
import type { Id } from '../../base-types';

const Debug = createDebug('TenancyLibrary:TenantService');

/**
 * Tenant Service
 *
 * Client-side service that manages the active tenant id in session storage,
 * keyed by user email so each user retains their own tenant selection.
 * Subscribes to the auth service's current user to update the storage key
 * when the user changes.
 */
@Injectable({ providedIn: 'root' })
export class TenantService {
  /** Active Tenant Id - The currently selected tenant id, or null */
  private activeTenantId!: Id | null;

  /** Current Email - The current user's email, used as the storage key suffix */
  private currentEmail!: string | null;

  authService = inject(AuthService);
  constructor() {
    this.initialize();
  }

  /** Initialize - Subscribe to current user and load tenant id */ initialize(): void {
    const debug = Debug.extend('initialize');
    debug('initializing');
    this.authService.currentUser.subscribe((user: any) => {
      const email = user?.email ?? null;
      debug('user changed %j', email);

      this.currentEmail = email;
      if (email) {
        // JSON round-trip preserves the id type (number or string, e.g. UUID)
        const stored = sessionStorage.getItem(`activeTenantId:${email}`);
        try {
          this.activeTenantId = stored ? JSON.parse(stored) : null;
        } catch {
          // Value stored by an older version without JSON encoding
          this.activeTenantId = stored as unknown as Id;
        }
      } else {
        this.activeTenantId = null;
      }
      debug('activeTenantId %j', this.activeTenantId);
    });
  }

  /** Get Active Tenant Id - Returns the currently selected tenant id, or null */
  getActiveTenantId(): Id | null {
    return this.activeTenantId;
  }

  /** Set Active Tenant - Sets the active tenant id in session storage for the current user */
  setActiveTenant(tenantId: Id): void {
    const debug = Debug.extend('setActiveTenant');
    debug('tenantId %j', tenantId);
    if (this.currentEmail) {
      sessionStorage.setItem(
        `activeTenantId:${this.currentEmail}`,
        JSON.stringify(tenantId),
      );
    }
    this.activeTenantId = tenantId;
  }

  /** Clear Active Tenant - Removes the active tenant id from session storage for the current user */
  clearActiveTenant(): void {
    const debug = Debug.extend('clearActiveTenant');
    debug('clearing');
    if (this.currentEmail) {
      sessionStorage.removeItem(`activeTenantId:${this.currentEmail}`);
    }
    this.activeTenantId = null;
  }
}
